/**
 * Live-Suche im SkillLock-Wiki (Spec 9.4) neben dem Antwort-Editor.
 *
 * Dieselbe Suche, die auch `collectDraftContext()` benutzt — der Agent sieht
 * damit genau das, was ein KI-Entwurf sehen wuerde. Die Vorbelegung kommt aus
 * dem Ticketbetreff; die Stichwortbildung passiert auf dem Server (dieselbe
 * reine Funktion aus `shared`), und die tatsaechlich gestellte Anfrage steht
 * unter dem Feld — sonst raetselt man, warum nichts kommt.
 */
import type { SkilllockWikiHit } from '@tickethub/shared';
import { BookOpen, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type WikiSearchProps = {
  /** `false` = SkillLock ist fuer diesen Mandanten nicht eingerichtet. */
  enabled: boolean;
  initialQuery: string;
  onSearch: (query: string) => Promise<{ query: string; items: SkilllockWikiHit[] }>;
};

export function WikiSearch({ enabled, initialQuery, onSearch }: WikiSearchProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState(initialQuery);
  const [result, setResult] = useState<{ query: string; items: SkilllockWikiHit[] } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!enabled) return null;

  async function search() {
    if (input.trim().length < 2) return;
    setBusy(true);
    try {
      setResult(await onSearch(input.trim()));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-2 rounded-lg border p-4" aria-label={t('wiki.title')}>
      <h2 className="flex items-center gap-1 text-sm font-medium">
        <BookOpen aria-hidden="true" className="size-4" />
        {t('wiki.title')}
      </h2>

      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void search();
        }}
      >
        <Input
          type="search"
          value={input}
          placeholder={t('wiki.placeholder')}
          onChange={(event) => setInput(event.target.value)}
        />
        <Button type="submit" size="sm" variant="outline" disabled={busy}>
          <Search aria-hidden="true" />
          {t('wiki.search')}
        </Button>
      </form>

      {result === null ? null : result.items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('wiki.empty', { query: result.query })}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{t('wiki.query', { query: result.query })}</p>
          <ul className="space-y-2">
            {result.items.map((hit) => (
              <li key={hit.id} className="text-sm">
                {hit.url ? (
                  <a className="font-medium hover:underline" href={hit.url} target="_blank" rel="noreferrer">
                    {hit.title}
                  </a>
                ) : (
                  <span className="font-medium">{hit.title}</span>
                )}
                <p className="line-clamp-3 text-xs text-muted-foreground">{hit.excerpt}</p>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
